<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { cn } from '@/lib/utils'
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/shadcn/resizable'
import { useBreakpoint } from '@/composables/useBreakpoint'
import type { SplitterPanel } from 'reka-ui'
import type { SplitterSizes, JSplitterMethods } from '@/types/splitter.types'

type Orientation = 'horizontal' | 'vertical'

const props = withDefaults(
  defineProps<{
    /** 분할 방향 (horizontal: 좌우, vertical: 상하) */
    direction?: Orientation
    /** 첫 번째 패널의 기본 크기 (%) */
    defaultSize?: number
    /** 첫 번째 패널의 최소 크기 (%) */
    minSize?: number
    /** 첫 번째 패널의 최대 크기 (%) */
    maxSize?: number
    /** 두 번째 패널의 최소 크기 (%) */
    secondMinSize?: number
    /** 두 번째 패널의 최대 크기 (%) */
    secondMaxSize?: number
    /** ResizableHandle에 grip 아이콘 표시 여부 */
    withHandle?: boolean
    /** 패널 간 여백 (px) */
    gap?: number
    /** 추가 CSS 클래스 */
    class?: string
    /** 모바일에서 자동 수직 전환 (기본: true) */
    responsive?: boolean
    /** v-model:sizes - [firstSize, secondSize], 합계는 100이어야 함 */
    sizes?: SplitterSizes
    /** 첫 번째 패널 collapsible 여부 */
    collapsibleFirst?: boolean
    /** 두 번째 패널 collapsible 여부 */
    collapsibleSecond?: boolean
    /** collapsible 시 첫 번째 패널 축소 크기 (%) */
    collapsedSizeFirst?: number
    /** collapsible 시 두 번째 패널 축소 크기 (%) */
    collapsedSizeSecond?: number
    /** collapse/expand 시 슬라이드 애니메이션 여부 */
    animated?: boolean
    /** 슬라이드 애니메이션 지속 시간 (ms) */
    animationDuration?: number
  }>(),
  {
    direction: 'horizontal',
    defaultSize: 40,
    minSize: 20,
    withHandle: false,
    gap: 2,
    responsive: true,
    collapsibleFirst: false,
    collapsibleSecond: false,
    collapsedSizeFirst: 0,
    collapsedSizeSecond: 0,
    animated: true,
    animationDuration: 300,
  },
)

const emit = defineEmits<{
  'update:sizes': [sizes: SplitterSizes]
  'resize': [sizes: SplitterSizes, source: 'drag' | 'api' | 'prop']
}>()

const { isMobile } = useBreakpoint()

// 모바일에서 horizontal → vertical 자동 전환
const effectiveDirection = computed<Orientation>(() => {
  if (props.responsive && isMobile.value && props.direction === 'horizontal') {
    return 'vertical'
  }
  return props.direction
})

// 모바일 수직 전환 시 50:50 균등 분할
const effectiveDefaultSize = computed(() => {
  if (props.responsive && isMobile.value && props.direction === 'horizontal') {
    return 50
  }
  return props.defaultSize
})

// 두 번째 패널의 기본 크기 계산
const secondDefaultSize = computed(() => 100 - effectiveDefaultSize.value)

// ── Template Refs ────────────────────────────────────────────────────
const firstPanelRef = ref<InstanceType<typeof SplitterPanel> | null>(null)
const secondPanelRef = ref<InstanceType<typeof SplitterPanel> | null>(null)

// ── collapse/expand 자체 상태 — reka-ui 타이밍 의존 제거 ─────────────
const _firstCollapsedSize = ref(0)
const _secondCollapsedSize = ref(0)
const _isFirstCollapsed = ref(false)
const _isSecondCollapsed = ref(false)
const _isAnimating = ref(false)

// ── 프로그래매틱 collapse 시 min/maxSize 임시 제거 ─────────────────────
// reka-ui에 collapsible을 전달하지 않으므로 드래그는 항상 min/maxSize를 지킴
// collapse API 호출 시에만 제약을 임시 해제:
//   collapseFirst → firstMin=0, secondMax=100 (두 번째 패널이 100%까지 확장 허용)
//   collapseSecond → secondMin=0, firstMax=100 (첫 번째 패널이 100%까지 확장 허용)
const _minSizeFirstOverride = ref<number | undefined>(undefined)
const _minSizeSecondOverride = ref<number | undefined>(undefined)
const _maxSizeFirstOverride = ref<number | undefined>(undefined)
const _maxSizeSecondOverride = ref<number | undefined>(undefined)
const _effectiveMinSizeFirst = computed(() => _minSizeFirstOverride.value ?? props.minSize)
const _effectiveMinSizeSecond = computed(() => _minSizeSecondOverride.value ?? props.secondMinSize)
const _effectiveMaxSizeFirst = computed(() => _maxSizeFirstOverride.value ?? props.maxSize)
const _effectiveMaxSizeSecond = computed(() => _maxSizeSecondOverride.value ?? props.secondMaxSize)

// ── 무한 루프 방지 플래그 ─────────────────────────────────────────────
// prop→resize() 호출 중일 때 @layout 핸들러가 update:sizes를 emit하지 않도록 차단
let isUpdatingFromProp = false

// reka-ui resize() 후 onLayout이 부동소수점 오차(~0.001)로 발화될 수 있어
// collapsedSize와 단순 비교하면 collapse 직후 isCollapsed가 잘못 리셋됨.
// 드래그로 진짜 열리는 경우를 구분할 수 있는 최소 임계값.
const COLLAPSE_OPEN_THRESHOLD = 0.5

// ── @layout 핸들러 (드래그 포함 모든 크기 변경) ────────────────────────
function handleLayout(layout: number[]) {
  if (isUpdatingFromProp) return

  // 드래그로 collapsed 패널이 열리면 상태 동기화 + min/maxSize 복원
  // (부동소수점 오차 방지: collapsedSize + COLLAPSE_OPEN_THRESHOLD 초과 시에만 열린 것으로 판단)
  if (_isFirstCollapsed.value && (layout[0] ?? 0) > props.collapsedSizeFirst + COLLAPSE_OPEN_THRESHOLD) {
    _isFirstCollapsed.value = false
    if ((layout[0] ?? 0) >= (props.minSize ?? 0)) {
      _minSizeFirstOverride.value = undefined
    }
    _maxSizeSecondOverride.value = undefined
  }
  if (_isSecondCollapsed.value && (layout[1] ?? 0) > props.collapsedSizeSecond + COLLAPSE_OPEN_THRESHOLD) {
    _isSecondCollapsed.value = false
    if ((layout[1] ?? 0) >= (props.secondMinSize ?? 0)) {
      _minSizeSecondOverride.value = undefined
    }
    _maxSizeFirstOverride.value = undefined
  }

  const sizes: SplitterSizes = [
    Math.round((layout[0] ?? 0) * 10) / 10,
    Math.round((layout[1] ?? 0) * 10) / 10,
  ]
  emit('update:sizes', sizes)
  emit('resize', sizes, 'drag')
}

// ── sizes prop watch ──────────────────────────────────────────────────
watch(
  () => props.sizes,
  async (newSizes) => {
    if (!newSizes || !firstPanelRef.value) return
    // 모바일 responsive 방향 전환 중이면 sizes prop 무시
    if (props.responsive && isMobile.value && props.direction === 'horizontal') return
    if (Math.abs(newSizes[0] + newSizes[1] - 100) > 0.1) {
      console.warn('[JSplitter] sizes 합계가 100이 아닙니다:', newSizes)
      return
    }
    isUpdatingFromProp = true
    try {
      firstPanelRef.value.resize(newSizes[0])
      await nextTick()
    } finally {
      isUpdatingFromProp = false
    }
    emit('resize', newSizes, 'prop')
  },
  { deep: true },
)

// ── 모바일 방향 전환 시 layout 이벤트 억제 ──────────────────────────────
watch(effectiveDirection, async () => {
  isUpdatingFromProp = true
  await nextTick()
  await nextTick()
  isUpdatingFromProp = false
})

// ── 명령형 API 헬퍼 ─────────────────────────────────────────────────
function getSizes(): SplitterSizes {
  return [
    Math.round((firstPanelRef.value?.getSize() ?? effectiveDefaultSize.value) * 10) / 10,
    Math.round((secondPanelRef.value?.getSize() ?? secondDefaultSize.value) * 10) / 10,
  ]
}

async function resizeFirst(size: number) {
  if (!firstPanelRef.value) return
  isUpdatingFromProp = true
  try {
    firstPanelRef.value.resize(size)
    await nextTick()
  } finally {
    isUpdatingFromProp = false
  }
  const sizes: SplitterSizes = [size, 100 - size]
  emit('update:sizes', sizes)
  emit('resize', sizes, 'api')
}

async function resizeSecond(size: number) {
  if (!secondPanelRef.value) return
  isUpdatingFromProp = true
  try {
    secondPanelRef.value.resize(size)
    await nextTick()
  } finally {
    isUpdatingFromProp = false
  }
  const sizes: SplitterSizes = [100 - size, size]
  emit('update:sizes', sizes)
  emit('resize', sizes, 'api')
}

async function resetToDefault() {
  await resizeFirst(effectiveDefaultSize.value)
}

const isFirstCollapsed = computed(() => _isFirstCollapsed.value)
const isSecondCollapsed = computed(() => _isSecondCollapsed.value)

async function collapseFirst() {
  if (!firstPanelRef.value || !props.collapsibleFirst) return
  const current = firstPanelRef.value.getSize()
  if (current > props.collapsedSizeFirst) _firstCollapsedSize.value = current
  if (props.animated) _isAnimating.value = true
  // nextTick 동안 reka-ui가 minSize 변경을 감지해 layout을 재계산하면 _firstCollapsedSize가
  // defaultSize로 덮어써지므로, nextTick이 끝날 때까지 handleLayout 추적을 차단
  isUpdatingFromProp = true
  _minSizeFirstOverride.value = 0
  _maxSizeSecondOverride.value = 100
  await nextTick()
  isUpdatingFromProp = false
  await resizeFirst(props.collapsedSizeFirst)
  _isFirstCollapsed.value = true
  if (props.animated) {
    await new Promise<void>(resolve => setTimeout(resolve, props.animationDuration))
    _isAnimating.value = false
  }
}

async function expandFirst() {
  if (!firstPanelRef.value) return
  const restoreSize = _firstCollapsedSize.value || effectiveDefaultSize.value
  if (props.animated) _isAnimating.value = true
  await resizeFirst(restoreSize)
  // min/maxSize 복원 후 reka-ui가 레이아웃을 defaultSize로 재계산할 수 있으므로
  // nextTick 후 다시 한번 size를 강제 적용
  _minSizeFirstOverride.value = undefined
  _maxSizeSecondOverride.value = undefined
  await nextTick()
  await resizeFirst(restoreSize)
  _isFirstCollapsed.value = false
  if (props.animated) {
    await new Promise<void>(resolve => setTimeout(resolve, props.animationDuration))
    _isAnimating.value = false
  }
}

async function collapseSecond() {
  if (!secondPanelRef.value || !props.collapsibleSecond) return
  const current = secondPanelRef.value.getSize()
  if (current > props.collapsedSizeSecond) _secondCollapsedSize.value = current
  if (props.animated) _isAnimating.value = true
  isUpdatingFromProp = true
  _minSizeSecondOverride.value = 0
  _maxSizeFirstOverride.value = 100
  await nextTick()
  isUpdatingFromProp = false
  await resizeSecond(props.collapsedSizeSecond)
  _isSecondCollapsed.value = true
  if (props.animated) {
    await new Promise<void>(resolve => setTimeout(resolve, props.animationDuration))
    _isAnimating.value = false
  }
}

async function expandSecond() {
  if (!secondPanelRef.value) return
  const restoreSize = _secondCollapsedSize.value || secondDefaultSize.value
  if (props.animated) _isAnimating.value = true
  await resizeSecond(restoreSize)
  _minSizeSecondOverride.value = undefined
  _maxSizeFirstOverride.value = undefined
  await nextTick()
  await resizeSecond(restoreSize)
  _isSecondCollapsed.value = false
  if (props.animated) {
    await new Promise<void>(resolve => setTimeout(resolve, props.animationDuration))
    _isAnimating.value = false
  }
}

defineExpose<JSplitterMethods>({
  resizeFirst,
  resizeSecond,
  getSizes,
  collapseFirst,
  expandFirst,
  collapseSecond,
  expandSecond,
  resetToDefault,
  isFirstCollapsed,
  isSecondCollapsed,
})
</script>

<template>
  <ResizablePanelGroup
    :direction="effectiveDirection"
    :class="cn('jsplitter-group min-h-0 min-w-0', $props.class)"
    @layout="handleLayout"
  >
    <!-- 첫 번째 패널 (좌측/상단) -->
    <ResizablePanel
      ref="firstPanelRef"
      :default-size="effectiveDefaultSize"
      :min-size="_effectiveMinSizeFirst"
      :max-size="_effectiveMaxSizeFirst"
      :class="gap > 0 ? (effectiveDirection === 'horizontal' ? 'pr-[calc(var(--gap)/2)]' : 'pb-[calc(var(--gap)/2)]') : ''"
      :style="[gap > 0 ? { '--gap': `${gap}px` } : {}, _isAnimating ? { transition: `flex ${animationDuration}ms ease` } : {}]"
    >
      <slot name="first" />
      <!-- direction="horizontal"일 때 left, vertical일 때 top으로도 사용 가능 -->
      <slot name="left" />
      <slot name="top" />
    </ResizablePanel>

    <!-- 크기 조정 핸들 -->
    <ResizableHandle :with-handle="withHandle" class="jsplitter-handle" />

    <!-- 두 번째 패널 (우측/하단) -->
    <ResizablePanel
      ref="secondPanelRef"
      :default-size="secondDefaultSize"
      :min-size="_effectiveMinSizeSecond"
      :max-size="_effectiveMaxSizeSecond"
      :class="gap > 0 ? (effectiveDirection === 'horizontal' ? 'pl-[calc(var(--gap)/2)]' : 'pt-[calc(var(--gap)/2)]') : ''"
      :style="[gap > 0 ? { '--gap': `${gap}px` } : {}, _isAnimating ? { transition: `flex ${animationDuration}ms ease` } : {}]"
    >
      <slot name="second" />
      <!-- direction="horizontal"일 때 right, vertical일 때 bottom으로도 사용 가능 -->
      <slot name="right" />
      <slot name="bottom" />
    </ResizablePanel>
  </ResizablePanelGroup>
</template>

<style scoped>
/* ── overflow 보호 ──────────────────────────────────────────────────── */
:deep(.jsplitter-group [data-panel]) {
  min-width: 0;
  min-height: 0;
}

/* ── horizontal 핸들: 8px 히트영역 ─────────────────────────────────── */
:deep(.jsplitter-handle[data-orientation="horizontal"]) {
  width: 8px !important;
  background: transparent !important;
  position: relative;
  z-index: 3;
  transition: background 0.15s ease;
}

/* ── vertical 핸들: 8px 히트영역 ───────────────────────────────────── */
:deep(.jsplitter-handle[data-orientation="vertical"]) {
  height: 8px !important;
  background: transparent !important;
  position: relative;
  z-index: 3;
  transition: background 0.15s ease;
}

/* ── 시각선: 1px (after:w-1 override) ──────────────────────────────── */
:deep(.jsplitter-handle::after) {
  width: 1px !important;
  background: hsl(var(--border)) !important;
  transition: background 0.15s ease, width 0.12s ease !important;
}

:deep(.jsplitter-handle[data-orientation="vertical"]::after) {
  height: 1px !important;
  width: 100% !important;
}

/* ── 그립 도트 인디케이터 (::before) ───────────────────────────────── */
:deep(.jsplitter-handle::before) {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3px;
  height: 20px;
  background-image: radial-gradient(circle, hsl(var(--muted-foreground) / 0.5) 1px, transparent 1px);
  background-size: 3px 6px;
  background-repeat: repeat-y;
  border-radius: 2px;
  opacity: 0.5;
  transition: opacity 0.15s ease;
  pointer-events: none;
  z-index: 4;
}

:deep(.jsplitter-handle[data-orientation="vertical"]::before) {
  width: 20px;
  height: 3px;
  background-size: 6px 3px;
  background-repeat: repeat-x;
  background-image: radial-gradient(circle, hsl(var(--muted-foreground) / 0.5) 1px, transparent 1px);
}

/* ── hover ──────────────────────────────────────────────────────────── */
:deep(.jsplitter-handle:hover) {
  background: hsl(var(--primary) / 0.04) !important;
}

:deep(.jsplitter-handle:hover::after) {
  width: 2px !important;
  background: hsl(var(--primary) / 0.55) !important;
}

:deep(.jsplitter-handle:hover::before) {
  opacity: 1;
}

/* ── drag / active ──────────────────────────────────────────────────── */
:deep(.jsplitter-handle[data-state="drag"]),
:deep(.jsplitter-handle:active) {
  background: hsl(var(--primary) / 0.06) !important;
}

:deep(.jsplitter-handle[data-state="drag"]::after),
:deep(.jsplitter-handle:active::after) {
  width: 2px !important;
  background: hsl(var(--primary)) !important;
}

:deep(.jsplitter-handle[data-state="drag"]::before),
:deep(.jsplitter-handle:active::before) {
  opacity: 1;
  background-image: radial-gradient(circle, hsl(var(--primary) / 0.65) 1px, transparent 1px);
}
</style>
